import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const ToySelectionAnimation: React.FC = () => {
  const containerRef = useRef<SVGSVGElement>(null);
  const toy1Ref = useRef<SVGGElement>(null);
  const toy2Ref = useRef<SVGGElement>(null);
  const cursorRef = useRef<SVGGElement>(null);
  const glowRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.5 });

    // helper to compute the current center of a toy group in SVG user units
    const getGroupCenter = (g: SVGGElement | null) => {
      if (!g) return { x: 0, y: 0 };
      const bbox = g.getBBox();
      const m = g.transform.baseVal.consolidate()?.matrix;
      const tx = m?.e || 0;
      const ty = m?.f || 0;
      return {
        x: tx + bbox.x + bbox.width / 2,
        y: ty + bbox.y + bbox.height / 2,
      };
    };

    // variable that will be read at runtime by GSAP for cursor target
    let clickPos = { x: 0, y: 0 };

    // Reset all states instantly at start of each loop
    tl.set([toy1Ref.current, toy2Ref.current], { y: -200, opacity: 1 });
    tl.set(cursorRef.current, { x: 500, y: 350, opacity: 0 });
    tl.set(glowRef.current, {
      scale: 0,
      opacity: 0,
      transformOrigin: "center",
    });

    // Drop toys
    tl.to([toy1Ref.current, toy2Ref.current], {
      y: 250,
      ease: "bounce.out",
      duration: 0.8,
      stagger: 0.15,
    });

    // Pause before cursor enters
    tl.to({}, { duration: 0.3 });

    // Cursor enters & clicks toy1
    tl.add(() => {
      // compute center at the moment of click (after bounce)
      clickPos = getGroupCenter(toy1Ref.current);
      // align glow ring center
      gsap.set(glowRef.current, { attr: { cx: clickPos.x, cy: clickPos.y } });
    });
    tl.to(cursorRef.current, { opacity: 1, duration: 0.2 }, "<");
    tl.to(
      cursorRef.current,
      {
        x: () => clickPos.x,
        y: () => clickPos.y,
        duration: 0.8,
        ease: "power2.out",
      },
      "<"
    );
    tl.to(glowRef.current, {
      scale: 1.2,
      opacity: 1,
      duration: 0.2,
      ease: "back.out(3)",
    });
    tl.to(glowRef.current, { scale: 1, duration: 0.1 }, ">");

    // Fade out toy2
    tl.to(toy2Ref.current, { opacity: 0, duration: 0.4 }, "<0.1");

    // Hold a moment
    tl.to({}, { duration: 0.8 });

    // Fade everything for reset
    tl.to([toy1Ref.current, cursorRef.current, glowRef.current], {
      opacity: 0,
      duration: 0.4,
    });
  }, []);

  return (
    <svg
      ref={containerRef}
      viewBox="0 0 800 600" // 4:3 aspect
      className="w-full h-auto"
    >
      <rect width="800" height="600" fill="#fff7ed" />

      {/* Glow ring */}
      <circle
        ref={glowRef}
        cx="250"
        cy="300"
        r="80"
        fill="none"
        stroke="#22c55e"
        strokeWidth="8"
        opacity="0"
      />

      {/* Toy 1 */}
      <g ref={toy1Ref} transform="translate(200,250)">
        <rect x="0" y="0" width="100" height="100" rx="20" fill="#a65430" />
        <circle cx="50" cy="50" r="20" fill="#fff" />
      </g>

      {/* Toy 2 */}
      <g ref={toy2Ref} transform="translate(450,550)">
        <rect x="0" y="0" width="100" height="100" rx="20" fill="#f59e0b" />
        <rect x="35" y="35" width="30" height="30" fill="#fff" />
      </g>

      {/* Cursor */}
      <g ref={cursorRef} opacity="0">
        <path
          d="M0 0 L0 30 L10 20 L20 50 L28 46 L18 16 L34 16 Z"
          fill="#111827"
        />
      </g>
    </svg>
  );
};

export default ToySelectionAnimation;
